let cachedUserAgent;
let cachedDeviceInfoModule;
let cachedReactNativeModule;
let cachedPlatformConstantsModule;
export function ensureReactNativeUserAgent(headers) {
    if (!isReactNativeRuntime()) {
        return;
    }
    const existingKey = Object.keys(headers).find((key) => typeof key === 'string' && key.toLowerCase() === 'user-agent');
    const headerKey = existingKey || 'User-Agent';
    const userAgent = getReactNativeUserAgent();
    if (userAgent) {
        headers[headerKey] = userAgent;
    }
}
export function getReactNativeUserAgent() {
    if (typeof cachedUserAgent !== 'undefined') {
        return cachedUserAgent || undefined;
    }
    const deviceInfo = getReactNativeDeviceInfoModule();
    const platformConstants = getPlatformConstants();
    const androidUserAgent = getAndroidUserAgent(deviceInfo, platformConstants);
    if (androidUserAgent) {
        cachedUserAgent = androidUserAgent;
        return androidUserAgent;
    }
    const appName = getStringValue(deviceInfo && typeof deviceInfo.appName === 'string'
        ? () => deviceInfo.appName
        : deviceInfo && typeof deviceInfo.getApplicationName === 'function'
            ? deviceInfo.getApplicationName.bind(deviceInfo)
            : undefined);
    const appVersion = getStringValue(deviceInfo && typeof deviceInfo.appVersion === 'string'
        ? () => deviceInfo.appVersion
        : deviceInfo && typeof deviceInfo.getVersion === 'function'
            ? deviceInfo.getVersion.bind(deviceInfo)
            : undefined) ||
        getStringValue(deviceInfo && typeof deviceInfo.getReadableVersion === 'function'
            ? deviceInfo.getReadableVersion.bind(deviceInfo)
            : undefined);
    const model = getStringValue(deviceInfo && typeof deviceInfo.model === 'string'
        ? () => deviceInfo.model
        : deviceInfo && typeof deviceInfo.getModel === 'function'
            ? deviceInfo.getModel.bind(deviceInfo)
            : platformConstants && typeof platformConstants.Model === 'string'
                ? () => platformConstants.Model
                : platformConstants && typeof platformConstants.model === 'string'
                    ? () => platformConstants.model
                    : undefined);
    const machine = getStringValue(deviceInfo && typeof deviceInfo.deviceId === 'string'
        ? () => deviceInfo.deviceId
        : deviceInfo && typeof deviceInfo.getDeviceId === 'function'
            ? deviceInfo.getDeviceId.bind(deviceInfo)
            : undefined);
    const systemVersion = getStringValue(deviceInfo && typeof deviceInfo.systemVersion === 'string'
        ? () => deviceInfo.systemVersion
        : deviceInfo && typeof deviceInfo.getSystemVersion === 'function'
            ? deviceInfo.getSystemVersion.bind(deviceInfo)
            : platformConstants && typeof platformConstants.osVersion === 'string'
                ? () => platformConstants.osVersion
                : platformConstants && typeof platformConstants.Release === 'string'
                    ? () => platformConstants.Release
                    : undefined) || getPlatformVersion(platformConstants);
    const parts = [appName, appVersion, model, machine, systemVersion].filter((part) => typeof part === 'string' && part.trim().length > 0);
    const builtUserAgent = normalizeUserAgent(parts.join('/'));
    cachedUserAgent = builtUserAgent.length ? builtUserAgent : null;
    return cachedUserAgent || undefined;
}
function isReactNativeRuntime() {
    if (typeof navigator !== 'undefined' && navigator.product === 'ReactNative') {
        return true;
    }
    if (typeof global !== 'undefined' && global.__fbBatchedBridge) {
        return true;
    }
    return false;
}
function getAndroidUserAgent(deviceInfo, platformConstants) {
    const okHttpUserAgent = getStringValue(deviceInfo && typeof deviceInfo.getUserAgentSync === 'function'
        ? deviceInfo.getUserAgentSync.bind(deviceInfo)
        : undefined) ||
        (typeof navigator !== 'undefined' && typeof navigator.userAgent === 'string'
            ? navigator.userAgent
            : undefined);
    if (!okHttpUserAgent) {
        return undefined;
    }
    if (/Android/i.test(okHttpUserAgent)) {
        return normalizeUserAgent(okHttpUserAgent);
    }
    if (!isAndroidPlatformConstants(platformConstants)) {
        return normalizeUserAgent(okHttpUserAgent);
    }
    const release = getStringValue(platformConstants && typeof platformConstants.Release === 'string' ? () => platformConstants.Release : undefined);
    const model = getStringValue(platformConstants && typeof platformConstants.Model === 'string'
        ? () => platformConstants.Model
        : deviceInfo && typeof deviceInfo.model === 'string'
            ? () => deviceInfo.model
            : deviceInfo && typeof deviceInfo.getModel === 'function'
                ? deviceInfo.getModel.bind(deviceInfo)
                : undefined);
    const buildId = getStringValue(deviceInfo && typeof deviceInfo.getBuildIdSync === 'function'
        ? deviceInfo.getBuildIdSync.bind(deviceInfo)
        : undefined);
    if (release && model && buildId) {
        const androidUserAgent = `${okHttpUserAgent} (Linux; U; Android ${release}; ${model} Build/${buildId})`;
        return normalizeUserAgent(androidUserAgent);
    }
    return normalizeUserAgent(okHttpUserAgent);
}
function isAndroidPlatformConstants(platformConstants) {
    return Boolean(platformConstants &&
        (platformConstants.Release || platformConstants.Brand || platformConstants.Manufacturer || platformConstants.Model));
}
function normalizeUserAgent(value) {
    let normalized = value;
    try {
        normalized = normalized.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    }
    catch (error) {
        // ignore normalization errors
    }
    return normalized.replace(/[^\x20-\x7E]/g, '');
}
function getStringValue(getter) {
    if (!getter) {
        return undefined;
    }
    try {
        const value = getter();
        return typeof value === 'string' ? value : undefined;
    }
    catch (error) {
        return undefined;
    }
}
function getPlatformVersion(platformConstants) {
    if (!platformConstants || typeof platformConstants.Version === 'undefined' || platformConstants.Version === null) {
        return undefined;
    }
    try {
        return String(platformConstants.Version);
    }
    catch (error) {
        return undefined;
    }
}
function getReactNativeDeviceInfoModule() {
    if (typeof cachedDeviceInfoModule !== 'undefined') {
        return cachedDeviceInfoModule;
    }
    const nativeModule = getNativeModule('RNDeviceInfo');
    const module = nativeModule || optionalRequire('react-native-device-info');
    const resolved = module && module.default ? module.default : module;
    cachedDeviceInfoModule = resolved;
    return resolved;
}
function getReactNativeModule() {
    if (typeof cachedReactNativeModule !== 'undefined') {
        return cachedReactNativeModule;
    }
    const module = optionalRequire('react-native');
    const resolved = module && module.default ? module.default : module;
    cachedReactNativeModule = resolved;
    return resolved;
}
function getPlatformConstants() {
    if (typeof cachedPlatformConstantsModule !== 'undefined') {
        return cachedPlatformConstantsModule;
    }
    const nativeModule = getNativeModule('PlatformConstants');
    const reactNative = getReactNativeModule();
    const platformConstants = nativeModule ||
        (reactNative && reactNative.Platform && reactNative.Platform.constants ? reactNative.Platform.constants : undefined);
    cachedPlatformConstantsModule = platformConstants;
    return platformConstants;
}
function getNativeModule(name) {
    const globalAny = globalThis;
    try {
        if (globalAny && typeof globalAny.__turboModuleProxy === 'function') {
            const module = globalAny.__turboModuleProxy(name);
            if (module) {
                return module;
            }
        }
    }
    catch (error) {
        /* ignore */
    }
    try {
        if (globalAny && globalAny.nativeModuleProxy && globalAny.nativeModuleProxy[name]) {
            return globalAny.nativeModuleProxy[name];
        }
    }
    catch (error) {
        /* ignore */
    }
    return undefined;
}
function optionalRequire(moduleName) {
    try {
        const globalAny = globalThis;
        if (globalAny && typeof globalAny.require === 'function') {
            return globalAny.require(moduleName);
        }
    }
    catch (error) {
        /* ignore */
    }
    try {
        const globalAny = global;
        if (globalAny && typeof globalAny.require === 'function') {
            return globalAny.require(moduleName);
        }
    }
    catch (error) {
        /* ignore */
    }
    return undefined;
}
